/**
 * Scoring Types — domain correctness hierarchy for PadCanvas.
 *
 * These represent the server-side scoring result for each question type.
 * They are NOT LLM output types — they are computed by scorers after
 * LLM output has been parsed and converted.
 */

import type { SpeedCategory } from './feedbackTypes';

// ── Scorer-owned Value/Unit types ───────────────────────────────────

/** Scorer's value correctness (no 'not_found' — that's an LLM concern).
 *  Note: graded credit for an objective `partially_correct` answer is carried
 *  numerically (0/4/8 in defaultScorer → computeFinalScore), not as a band
 *  here — a former 'partial' value died with the lookupFinalScore 2D table. */
export type ValueCorrectness = 'correct' | 'close' | 'incorrect' | 'missing';

/** Scorer's unit correctness assessment */
export type UnitCorrectness = 'correct' | 'incorrect' | 'missing' | 'not_needed';

// ── Freeform Dimensions ─────────────────────────────────────────────

/** Freeform content quality dimension (from LLM assessment) */
export type FreeformContent = 'excellent' | 'good' | 'partial' | 'poor' | 'none';

/** Freeform reasoning quality dimension (from LLM assessment) */
export type FreeformReasoning = 'rigorous' | 'adequate' | 'weak' | 'partially_flawed' | 'flawed' | 'na';

// ── Answer Correctness Hierarchy ────────────────────────────────────

/** Base correctness result — every question type produces this */
export interface AnswerCorrectness {
    /** Integer 0-10. Absent ONLY on an answer result that is `unread`; the
     *  approach and speed results below narrow it back to required, because
     *  those are always computed. */
    score?: number;
    scoreComment?: string;  // reason why score might be low
    /** No answer could be read from the page, so nothing was scored. Not a 0:
     *  a 0 claims the answer was wrong. See
     *  docs/FeatureDesigns/solution-analysis/unread-answer-target.md. */
    unread?: true;
}

/** MCQ correctness — binary from option selection */
export interface McqCorrectness extends AnswerCorrectness {
    selectedOption: string;
    correctOption: string;
}

/** Numerical correctness — value match + unit match */
export interface NumericalCorrectness extends AnswerCorrectness {
    valueMatch: ValueCorrectness;
    unitMatch: UnitCorrectness;
    studentValue: NumericValue | null;
    correctValue: NumericValue | null;
}

/** Singleline correctness — text or numerical match */
export interface SinglelineCorrectness extends AnswerCorrectness {
    valueMatch: ValueCorrectness;
    matchMethod: 'text' | 'numerical' | 'llm';
}

/** Freeform correctness — content + reasoning dimensions */
export interface FreeformCorrectness extends AnswerCorrectness {
    content: FreeformContent;
    reasoning: FreeformReasoning;
}

// ── Score → correctness band (CANONICAL — single source of truth) ───
//
// Replaces the ad-hoc `>=7` / `>=8` / `===10` checks and duplicated
// `scoreClass` helpers that were scattered across every app. A 0-10 score maps
// to a qualitative band; label and color use the SAME thresholds so they can
// never disagree. Correctness is derived from the score here — NOT from the
// LLM's `overallAssessment`, which is computed independently and can contradict
// the score (the "Correct but 0/10" bug). Band names match the existing
// `overallAssessment` vocabulary so this is a drop-in.

/** A 0-10 score at or above this is correct. */
export const CORRECT_SCORE_THRESHOLD = 7;
/** A 0-10 score at or above this (but below correct) is partially correct. */
export const PARTIAL_SCORE_THRESHOLD = 4;

/** Qualitative correctness derived from a numeric score. Matches the
 *  `overallAssessment` vocabulary, plus `unknown` for a missing score. */
export type CorrectnessBand = 'correct' | 'partially_correct' | 'incorrect' | 'unknown';

/** Map a 0-10 score to a correctness band. `null`/`undefined` → 'unknown'. */
export function correctnessFromScore(score: number | null | undefined): CorrectnessBand {
    if (score == null) return 'unknown';
    if (score >= CORRECT_SCORE_THRESHOLD) return 'correct';
    if (score >= PARTIAL_SCORE_THRESHOLD) return 'partially_correct';
    return 'incorrect';
}

/** True when a score counts as correct (>= CORRECT_SCORE_THRESHOLD). Convenience
 *  for stats/counting call sites that just need a boolean. */
export function isCorrectScore(score: number | null | undefined): boolean {
    return score != null && score >= CORRECT_SCORE_THRESHOLD;
}

/**
 * A checked question's mark on a worksheet row: right, or not yet.
 *
 * Steps decide where there are steps — one step off the path is a question to
 * look at again, whatever the total came to. An objective answer checked against
 * the key has no steps, and then the score is all there is to read.
 *
 * One rule for the server, which marks a chapter's rows before the page draws,
 * and the client, which marks a row the moment its own check lands. Two copies
 * would let a row change colour on reload.
 */
export function worksheetVerdict(
    stepsCorrect: readonly boolean[],
    score: number | null | undefined,
): 'correct' | 'not-correct' {
    if (stepsCorrect.length > 0) return stepsCorrect.every(Boolean) ? 'correct' : 'not-correct';
    return isCorrectScore(score) ? 'correct' : 'not-correct';
}

/** Color/severity band for a score against a max (default 10). Mirrors the
 *  correctness thresholds (high >=7/10, mid >=4/10) so color matches the label. */
export type ScoreBand = 'high' | 'mid' | 'low';
export function scoreBand(score: number, max = 10): ScoreBand {
    const pct = max > 0 ? score / max : 0;
    if (pct >= CORRECT_SCORE_THRESHOLD / 10) return 'high';
    if (pct >= PARTIAL_SCORE_THRESHOLD / 10) return 'mid';
    return 'low';
}

// ── Numeric Value (from scoringHelpers) ─────────────────────────────

/** Parsed numeric answer with optional unit */
export interface NumericValue {
    value: number;
    unit: string | null;
}

// ── Approach Result ─────────────────────────────────────────────────

/** Scorer's approach assessment category (mapped from LLM's CorrectAnswerApproach/WrongAnswerApproach) */
export type ApproachCategory =
    | 'optimal'
    | 'suboptimal'
    | 'calculation_error'
    | 'partially_correct'
    | 'incomplete'
    | 'wrong_approach'
    | 'missing';

/** Approach scores by category */
export const APPROACH_SCORES: Record<ApproachCategory, number> = {
    optimal: 10,
    suboptimal: 8,
    calculation_error: 8,
    partially_correct: 7,
    incomplete: 6,
    wrong_approach: 0,
    missing: 0,
};

/** Scorer's approach result — mapped from LLM approach assessment */
export interface ApproachResult extends AnswerCorrectness {
    /** Always computed — an approach result is never `unread`. */
    score: number;
    category: ApproachCategory;
}

// ── Speed Result ──────────────────────────────────────────────────

/** Scorer's speed result — computed from time taken vs expected time */
export interface SpeedResult extends AnswerCorrectness {
    /** Always computed — a speed result is never `unread`. */
    score: number;
    speedCategory: SpeedCategory;
}
